import React from 'react';
import { RestaurantPeriodDelta } from '../../utils/restaurantPeriodDeltas';
import { NoDataInPeriod } from '../ui/NoDataInPeriod';

interface RestaurantDeltaCardProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  /** Background tint (hex+alpha) for the icon circle. */
  iconBg: string;
  /** Color for the change %% text. */
  changeColor: string;
  /** Items already ordered for display (risers: highest %% first, fallers: lowest %% first). */
  items: RestaurantPeriodDelta[];
  loading?: boolean;
}

const PREVIEW_ROWS = 5;

/**
 * "Top Riser" / "Biggest Faller" card for the all-restaurants Revenue page.
 * Matches the new UI: icon in a tinted circle, title + subtitle, and a simple list of
 * restaurant name + period-over-period change %% (no rank badge, no amount, no maximize).
 */
export const RestaurantDeltaCard: React.FC<RestaurantDeltaCardProps> = ({
  title,
  subtitle,
  icon,
  iconBg,
  changeColor,
  items,
  loading = false,
}) => {
  const rows = items.slice(0, PREVIEW_ROWS);

  return (
    <div className="bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040] min-w-0">
      <div className="flex items-center gap-2 mb-4">
        <span
          className="w-[42px] h-[42px] flex items-center justify-center rounded-full flex-shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          {icon}
        </span>
        <div className="min-w-0">
          <p className="text-[18px] leading-6 font-semibold text-textPrimary dark:text-textPrimary-dark mb-1 truncate">
            {title}
          </p>
          <p className="text-[14px] leading-5 font-normal text-textSecondary dark:text-textSecondary-dark">
            {subtitle}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3 py-2">
          {Array.from({ length: PREVIEW_ROWS }).map((_, i) => (
            <div key={i} className="h-6 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <NoDataInPeriod />
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map(item => (
            <div
              key={item.restaurantId}
              className="flex items-center justify-between p-2 border-b border-border dark:border-border-dark last:border-0"
            >
              <span className="text-[14px] leading-5 font-semibold text-textPrimary dark:text-textPrimary-dark truncate">
                {item.restaurantName}
              </span>
              <span className="text-[14px] leading-5 font-semibold flex-shrink-0" style={{ color: changeColor }}>
                {item.changePercent >= 0 ? '+' : ''}{item.changePercent.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RestaurantDeltaCard;
